// A pick of several countries (ISO alpha-2 codes), for the vendor's and the
// product's "ships to": the framework dropdown over its country list, with
// checkboxes and a search, styled like the other admin inputs.
import React from "react"
import { twMerge } from "tailwind-merge"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import { COUNTRIES } from "aldoo-ra/CountrySelect"

export const countryName = (code) => COUNTRIES.find((c) => c.value === code)?.name || code

export default function CountriesSelect({ value = [], onChange, disabled = false, placeholder = "Pick countries…", className = "", "data-testid": testId }) {
  const selected = COUNTRIES.filter((c) => value.includes(c.value))
  return (
    <div className={twMerge("inline-block align-middle w-full", className)} data-testid={testId} data-value={value.join(",")}>
      <DropdownSelect
        variant="admin"
        options={COUNTRIES}
        value={selected}
        placeholder={placeholder}
        onChange={(picked) => onChange?.((Array.isArray(picked) ? picked : [picked]).filter(Boolean).map((o) => o.value))}
        multiSelection
        showCheckbox
        searchEnabled
        enableResetButton
        allowEmptySelection
        disabled={disabled}
        styleOverrides={{
          button: "min-h-[38px] inline-flex items-center justify-between w-full rounded-md border border-admin_bordered px-3 py-1 bg-admin_paper text-sm font-normal text-admin_text outline-none focus:outline-none focus:border-admin_primary hover:bg-admin_paper transition-colors disabled:opacity-60",
          selectedText: "text-admin_text",
          resetButton: "absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-admin_text_secondary hover:text-admin_primary transition-colors text-xs",
        }}
      />
    </div>
  )
}

/**
 * One country (ISO alpha-2), for where a company is registered: the same
 * dropdown with a search, a single pick, the names in `language` (the site's).
 */
export function CountryPicker({ value = "", onChange, disabled = false, placeholder = "Pick a country…", language = "en", invalid = false, className = "", "data-testid": testId }) {
  let names = null
  try {
    names = new Intl.DisplayNames([language], { type: "region" })
  } catch {
    names = null
  }
  const options = COUNTRIES.map((c) => ({ ...c, name: names?.of(c.value) || c.name })).sort((a, b) => a.name.localeCompare(b.name, language))
  const selected = options.filter((c) => c.value === value)
  return (
    <div className={twMerge("inline-block align-middle w-full", className)} data-testid={testId} data-value={value}>
      <DropdownSelect
        variant="admin"
        options={options}
        value={selected}
        placeholder={placeholder}
        onChange={(picked) => {
          const choice = (Array.isArray(picked) ? picked : [picked]).filter(Boolean)[0]
          if (choice?.value) onChange?.(choice.value)
        }}
        searchEnabled
        // required: picked, never cleared
        enableResetButton={false}
        allowEmptySelection={false}
        showCheckbox={false}
        showSelectionTick
        disabled={disabled}
        styleOverrides={{
          button: twMerge(
            "min-h-[38px] inline-flex items-center justify-between w-full rounded-md border px-3 py-1 bg-admin_paper text-sm font-normal text-admin_text outline-none focus:outline-none focus:border-admin_primary hover:bg-admin_paper transition-colors disabled:opacity-60",
            invalid ? "border-admin_error" : "border-admin_bordered"
          ),
          selectedText: "text-admin_text",
        }}
      />
    </div>
  )
}
